import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { inject, ChangeDetectorRef, ViewContainerRef, Injector, EventEmitter, booleanAttribute, Directive, Input, Output, Component, ViewEncapsulation, ChangeDetectionStrategy, HostListener, NgModule } from '@angular/core';
import * as i1 from '@odx/angular';
import { CoreModule } from '@odx/angular';
import { A11yModule } from '@odx/angular/cdk/a11y';
import { DynamicViewDirective } from '@odx/angular/cdk/dynamic-view';
import * as i2 from '@odx/angular/components/loading-spinner';
import { LoadingSpinnerModule } from '@odx/angular/components/loading-spinner';
import { CSSComponent } from '@odx/angular/internal';
import { EventManager, injectElement, hasChanged } from '@odx/angular/utils';
import { ConnectedOverlayService } from '@odx/angular/cdk/connected-overlay';
import * as i3 from '@angular/cdk/a11y';

class CloseDropdownEvent extends Event {
    static { this.TYPE = 'odxDropdownClose'; }
    constructor() {
        super(CloseDropdownEvent.TYPE, { bubbles: true });
    }
}

const DefaultDropdownOptions = {
    position: 'bottom',
    matchReferenceWidth: false,
    enableFallback: true,
    minHeight: 0,
    offset: 6,
    outerPadding: 12,
};

const DEFAULT_DROPDOWN_OPEN_TRIGGERS = ['click', 'keydown.enter'];
const DEFAULT_DROPDOWN_CLOSE_TRIGGERS = ['keyup.esc'];
/**
 * Represents a directive that enables dropdown functionality in Angular.
 * This directive handles the opening and closing of a dropdown menu, as well as
 * managing the associated overlay and event handling.
 */
let DropdownDirective = class DropdownDirective {
    constructor() {
        this.changeDetector = inject(ChangeDetectorRef);
        this.connectedOverlayService = inject(ConnectedOverlayService);
        this.eventManager = inject(EventManager);
        this.viewContainer = inject(ViewContainerRef);
        this.injector = inject(Injector);
        this.overlayRef = null;
        this.element = injectElement();
        /**
         * Indicates whether the dropdown is disabled or not.
         *
         * @type {boolean}
         * @default false
         */
        this.disabled = false;
        /**
         * Indicates whether the loader should be shown.
         *
         * @type {boolean}
         * @default false
         */
        this.showLoader = false;
        /**
         * Determines whether the dropdown should close when a click event occurs outside of it.
         *
         * @type {boolean}
         * @default true
         */
        this.clickOutsideActive = true;
        /**
         * The host element to which the dropdown is appended.
         *
         * @type {HTMLElement | ViewContainerRef | null}
         */
        this.host = this.viewContainer;
        /**
         * The event triggers that open the dropdown.
         *
         * @type {string[] | null}
         * @default DEFAULT_DROPDOWN_OPEN_TRIGGERS
         */
        this.openTrigger = DEFAULT_DROPDOWN_OPEN_TRIGGERS;
        /**
         * The event triggers that close the dropdown.
         *
         * @type {string[] | null}
         * @default DEFAULT_DROPDOWN_CLOSE_TRIGGERS
         */
        this.closeTrigger = DEFAULT_DROPDOWN_CLOSE_TRIGGERS;
        /**
         * Event emitter that emits before the dropdown is opened.
         *
         * @emits {void}
         */
        this.beforeOpen = new EventEmitter();
        /**
         * Event emitter that emits after the dropdown is opened.
         *
         * @emits {void}
         */
        this.afterOpen = new EventEmitter();
        /**
         * Event emitter that emits before the dropdown is closed.
         *
         * @emits {void}
         */
        this.beforeClose = new EventEmitter();
        /**
         * Event emitter that emits after the dropdown is closed.
         *
         * @emits {void}
         */
        this.afterClose = new EventEmitter();
    }
    /**
     * Gets a value indicating whether the dropdown is currently open.
     *
     * @returns {boolean}
     */
    get isOpen() {
        return this.overlayRef !== null;
    }
    ngAfterViewInit() {
        this.eventManager.register(this.openTrigger ?? [], (event) => this.toggle(event), this.triggerElement ?? this.referenceElement);
        this.eventManager.register(this.closeTrigger ?? [], () => this.close(), 'document');
    }
    ngOnDestroy() {
        this.close();
    }
    ngOnChanges(changes) {
        if (hasChanged(changes, 'disabled') && this.disabled) {
            this.close();
        }
        if (hasChanged(changes, ['options', 'content', 'showLoader', 'clickOutsideActive'])) {
            this.overlayRef?.update(this.createOverlayOptions());
        }
    }
    /**
     * Toggles the state of the dropdown.
     * If the dropdown is open, it will be closed. If it is closed, it will be opened.
     *
     * @param {Event | undefined} event - The optional event that triggered the toggle.
     */
    toggle(event) {
        if (this.isOpen) {
            this.close(event);
        }
        else {
            this.open(event);
        }
    }
    /**
     * Opens the dropdown.
     *
     * @param {Event | undefined} event - The optional event that triggered the open.
     */
    open(event) {
        event?.preventDefault();
        if (this.isOpen || !this.content || this.disabled)
            return;
        this.overlayRef = this.connectedOverlayService.createOverlay(this.referenceElement ?? this.element.nativeElement, this.createOverlayOptions(), {
            host: this.referenceElement?.parentElement ?? this.host,
            injector: this.injector,
        });
        this.changeDetector.markForCheck();
    }
    /**
     * Closes the dropdown.
     *
     * @param {Event | undefined} event - The optional event that triggered the close.
     */
    close(event) {
        event?.preventDefault();
        this.overlayRef?.close();
        this.overlayRef = null;
        this.changeDetector.markForCheck();
    }
    createOverlayOptions() {
        const containerClass = ['odx-dropdown-overlay', this.options?.containerClass].filter(Boolean).join(' ');
        const context = {
            content: this.content,
            showLoader: this.showLoader,
            clickOutsideActive: this.clickOutsideActive,
        };
        return {
            ...DefaultDropdownOptions,
            ...this.options,
            fallbackAxisSideDirection: 'end',
            containerClass,
            content: DropdownComponent,
            context,
            hooks: {
                beforeOpen: () => this.beforeOpen.emit(),
                afterOpen: () => this.afterOpen.emit(),
                beforeClose: () => this.beforeClose.emit(),
                afterClose: () => this.afterClose.emit(),
            },
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "18.2.13", type: DropdownDirective, isStandalone: true, selector: "[odxDropdown]", inputs: { content: ["odxDropdown", "content"], disabled: ["odxDropdownDisabled", "disabled", booleanAttribute], showLoader: ["odxDropdownShowLoader", "showLoader", booleanAttribute], clickOutsideActive: ["odxDropdownClickOutsideActive", "clickOutsideActive", booleanAttribute], options: ["odxDropdownOptions", "options"], referenceElement: ["odxDropdownReferenceElement", "referenceElement"], triggerElement: ["odxDropdownTriggerElement", "triggerElement"], host: ["odxDropdownHost", "host"], openTrigger: ["odxDropdownOpenTrigger", "openTrigger"], closeTrigger: ["odxDropdownCloseTrigger", "closeTrigger"] }, outputs: { beforeOpen: "odxDropdownBeforeOpen", afterOpen: "odxDropdownAfterOpen", beforeClose: "odxDropdownBeforeClose", afterClose: "odxDropdownAfterClose" }, host: { properties: { "attr.aria-haspopup": "true", "attr.aria-expanded": "isOpen" } }, providers: [EventManager], exportAs: ["odxDropdown"], usesOnChanges: true, ngImport: i0 }); }
};
DropdownDirective = __decorate([
    CSSComponent('dropdown-host')
], DropdownDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[odxDropdown]',
                    exportAs: 'odxDropdown',
                    providers: [EventManager],
                    host: {
                        '[attr.aria-haspopup]': 'true',
                        '[attr.aria-expanded]': 'isOpen',
                    },
                }]
        }], propDecorators: { content: [{
                type: Input,
                args: ['odxDropdown']
            }], disabled: [{
                type: Input,
                args: [{ alias: 'odxDropdownDisabled', transform: booleanAttribute }]
            }], showLoader: [{
                type: Input,
                args: [{ alias: 'odxDropdownShowLoader', transform: booleanAttribute }]
            }], clickOutsideActive: [{
                type: Input,
                args: [{ alias: 'odxDropdownClickOutsideActive', transform: booleanAttribute }]
            }], options: [{
                type: Input,
                args: ['odxDropdownOptions']
            }], referenceElement: [{
                type: Input,
                args: ['odxDropdownReferenceElement']
            }], triggerElement: [{
                type: Input,
                args: ['odxDropdownTriggerElement']
            }], host: [{
                type: Input,
                args: ['odxDropdownHost']
            }], openTrigger: [{
                type: Input,
                args: ['odxDropdownOpenTrigger']
            }], closeTrigger: [{
                type: Input,
                args: ['odxDropdownCloseTrigger']
            }], beforeOpen: [{
                type: Output,
                args: ['odxDropdownBeforeOpen']
            }], afterOpen: [{
                type: Output,
                args: ['odxDropdownAfterOpen']
            }], beforeClose: [{
                type: Output,
                args: ['odxDropdownBeforeClose']
            }], afterClose: [{
                type: Output,
                args: ['odxDropdownAfterClose']
            }] } });

/**
 * Represents a dropdown component.
 */
let DropdownComponent = class DropdownComponent {
    constructor() {
        this.host = inject(DropdownDirective);
        this.injector = inject(Injector);
        this.element = injectElement();
    }
    onDropdownClose() {
        this.host.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: DropdownComponent, isStandalone: true, selector: "odx-dropdown", inputs: { content: "content", showLoader: "showLoader", clickOutsideActive: "clickOutsideActive" }, host: { listeners: { "odxDropdownClose": "onDropdownClose()" } }, ngImport: i0, template: "<div\n  class=\"odx-dropdown__inner\"\n  [odxLoadingSpinner]=\"showLoader\"\n  [odxClickOutsideActive]=\"clickOutsideActive\"\n  (odxClickOutside)=\"host.close()\"\n  cdkTrapFocus\n  cdkTrapFocusAutoCapture\n>\n  <ng-template [odxDynamicView]=\"content\" [odxDynamicViewInjector]=\"injector\" />\n</div>\n", dependencies: [{ kind: "ngmodule", type: CoreModule }, { kind: "directive", type: i1.ClickOutsideDirective, selector: "[odxClickOutside]", inputs: ["odxClickOutsideActive"], outputs: ["odxClickOutside"] }, { kind: "directive", type: DynamicViewDirective, selector: "ng-template[odxDynamicView]", inputs: ["odxDynamicView", "odxDynamicViewInjector", "odxDynamicViewContext"] }, { kind: "ngmodule", type: LoadingSpinnerModule }, { kind: "directive", type: i2.LoadingSpinnerDirective, selector: "[odxLoadingSpinner]", inputs: ["odxLoadingSpinnerAutoColor", "odxLoadingSpinner", "odxLoadingSpinnerMinHeight"] }, { kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i3.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
DropdownComponent = __decorate([
    CSSComponent('dropdown')
], DropdownComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'odx-dropdown', imports: [CoreModule, DynamicViewDirective, LoadingSpinnerModule, A11yModule], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"odx-dropdown__inner\"\n  [odxLoadingSpinner]=\"showLoader\"\n  [odxClickOutsideActive]=\"clickOutsideActive\"\n  (odxClickOutside)=\"host.close()\"\n  cdkTrapFocus\n  cdkTrapFocusAutoCapture\n>\n  <ng-template [odxDynamicView]=\"content\" [odxDynamicViewInjector]=\"injector\" />\n</div>\n" }]
        }], propDecorators: { content: [{
                type: Input
            }], showLoader: [{
                type: Input
            }], clickOutsideActive: [{
                type: Input
            }], onDropdownClose: [{
                type: HostListener,
                args: [CloseDropdownEvent.TYPE]
            }] } });

const modules = [DropdownDirective];
class DropdownModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: DropdownModule, imports: [DropdownDirective], exports: [CoreModule, DropdownDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownModule, imports: [CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: modules,
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * @internal
 * Dispatches a CloseDropdownEvent for target to close the current dropdown.
 *
 * @param {Element} target - The target element to close the dropdown for.
 */
function closeCurrentDropdown(target) {
    target.dispatchEvent(new CloseDropdownEvent());
}

/**
 * Generated bundle index. Do not edit.
 */

export { CloseDropdownEvent, DEFAULT_DROPDOWN_CLOSE_TRIGGERS, DEFAULT_DROPDOWN_OPEN_TRIGGERS, DefaultDropdownOptions, DropdownComponent, DropdownDirective, DropdownModule, closeCurrentDropdown };
